import React, { useMemo, useState } from "react";
import { api } from "../../lib/api.js";
import { StatStrip, FilterRow, PageHead, StatePill, Notice, fmtWhen } from "./ui.jsx";
import BatchDrawer from "./BatchDrawer.jsx";

const STATES = ["created", "sent", "opened", "registered", "bounced", "revoked"];

export default function InvitationsPage({ data, reload, isAdmin }) {
  const [who, setWho] = useState("all");
  const [state, setState] = useState("all");
  const [q, setQ] = useState("");
  const [wave, setWave] = useState(100);
  const [drawer, setDrawer] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [error, setError] = useState(null);

  const c = data.counts ?? {};
  const all = data.invitations ?? [];
  const rows = useMemo(() => all.filter(i =>
    (who === "all" || (who === "guest" ? i.kind === "guest" : i.kind !== "guest")) &&
    (state === "all" || i.state === state) &&
    (!q || `${i.name} ${i.email} ${i.codes ?? ""}`.toLowerCase().includes(q.toLowerCase()))), [all, who, state, q]);

  async function sendWave() {
    setBusy(true); setMsg(null); setError(null);
    try {
      const r = await api("/backoffice/invitations/send", { method: "POST", body: { limit: Number(wave) } });
      setMsg(`${r.queued} queued to send${r.remaining ? `; ${r.remaining} still unsent` : "; none left unsent"}. Watch the states settle before the next wave.`);
      await reload();
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  }
  async function revoke(inv) {
    if (!window.confirm(`Withdraw the invitation for ${inv.name}? Their link will stop working.`)) return;
    try { await api(`/backoffice/invitations/${inv.id}/revoke`, { method: "POST", body: {} }); await reload(); }
    catch (err) { setError(err.message); }
  }

  return (
    <>
      <PageHead title="Invitations" sub="Each invitation is a key. It only works for the account it was sent to, and only once. Send in waves and watch the states settle."
        aside={isAdmin && <button className="bo-btn primary" onClick={() => setDrawer(true)}>New batch</button>} />
      <StatStrip items={STATES.map(s => ({ k: s, v: c[s] ?? 0, color: s === "registered" ? "#2f6b3a" : s === "bounced" || s === "revoked" ? "#8a2a1d" : undefined }))} />
      <FilterRow label="Who" value={who} onChange={setWho} options={[
        { value: "all", label: "Everyone", count: all.length, tone: "dark" },
        { value: "lipari", label: "Lipari employees", count: all.filter(i => i.kind !== "guest").length },
        { value: "guest", label: "Guests", count: all.filter(i => i.kind === "guest").length },
      ]} />
      <div className="bo-filter-row">
        <FilterRow label="State" value={state} onChange={setState} options={[{ value: "all", label: "Any state", count: all.length, tone: "roma" }, ...STATES.map(s => ({ value: s, label: s, count: c[s] ?? 0 }))]} />
        <input className="bo-search" placeholder="Search name, email or code" value={q} onChange={e => setQ(e.target.value)} />
      </div>

      {isAdmin && (
        <div className="bo-bar">
          <b>{c.created ?? 0} unsent</b>
          <span className="muted">Send in waves of</span>
          <input className="bo-num" type="number" min={1} max={1000} value={wave} onChange={e => setWave(e.target.value)} />
          <div className="spacer" />
          <button className="bo-btn primary" disabled={busy || !(c.created > 0)} onClick={sendWave}>Send next {Math.min(Number(wave) || 0, c.created ?? 0)}</button>
        </div>
      )}
      {msg && <Notice tone="ok">{msg}</Notice>}
      {error && <Notice tone="bad">{error}</Notice>}

      <div className="bo-table inv">
        <div className="bo-th"><div>Name</div><div>Email</div><div>Seats</div><div>State</div><div>Sent</div><div>Opened</div><div>Code</div><div /></div>
        {rows.map(i => (
          <div className="bo-tr" key={i.id}>
            <div className="name"><b>{i.name}</b>{i.kind === "guest" && <span className="tag">guest</span>}</div>
            <div className="mono">{i.email}</div>
            <div className="mono">{i.travellersAllowed} / {i.badgesAllowed}</div>
            <div><StatePill state={i.state} />{i.bounceReason && <div className="muted small">{i.bounceReason}</div>}</div>
            <div className="muted">{fmtWhen(i.sentAt)}</div>
            <div className="muted">{fmtWhen(i.openedAt)}</div>
            <div className="mono code">{i.codes ?? ""}</div>
            <div className="actions">{isAdmin && ["created", "sent", "opened", "bounced"].includes(i.state) && <button className="bo-link" onClick={() => revoke(i)}>Withdraw</button>}</div>
          </div>
        ))}
        {!rows.length && <div className="bo-empty">{all.length ? "Nothing matches those filters." : "No invitations yet. Create a batch to start."}</div>}
      </div>
      <div className="bo-count">{rows.length} of {all.length}</div>

      {drawer && <BatchDrawer onClose={() => setDrawer(false)} onDone={async (m) => { setDrawer(false); setMsg(m); await reload(); }} />}
    </>
  );
}
